import type { Uuid } from '../types/api';
import type { IceConfiguration } from '../types/call';
import type { ClientConfig, NotificationConfig } from '../types/config';
import type { ManagedDevice } from '../types/device';
import type {
  ContactMatch,
  Profile,
  ProfilePatch,
  SearchEntityType,
  SearchResult,
} from '../types/user';
import type { SyncPage } from '../types/websocket';
import { multipartWithFile } from '../platform';
import type { LiniyaHttpClient } from '../http/client';

interface RawProfile {
  user_id: Uuid;
  display_name: string;
  effective_display_name: string;
  username: string;
  bio: string;
  profile_visibility: Profile['profileVisibility'];
  last_seen_visibility: Profile['lastSeenVisibility'];
  avatar_url: string | null;
  avatar_version: string;
  updated_at: string;
}

/**
 * The profile payload is the one place the API's snake_case is translated into
 * the camelCase the interface uses, because these fields are read in dozens of
 * components. Everything else keeps the server's own field names so that a
 * payload seen in the network tab matches the code that handles it.
 */
function toProfile(raw: RawProfile): Profile {
  return {
    userId: raw.user_id,
    displayName: raw.display_name,
    effectiveDisplayName: raw.effective_display_name,
    username: raw.username,
    bio: raw.bio,
    profileVisibility: raw.profile_visibility,
    lastSeenVisibility: raw.last_seen_visibility,
    avatarUrl: raw.avatar_url,
    avatarVersion: raw.avatar_version,
    updatedAt: raw.updated_at,
  };
}

export class ProfileApi {
  constructor(private readonly http: LiniyaHttpClient) {}

  async get(): Promise<Profile> {
    const response = await this.http.request<{ profile: RawProfile }>('/api/v1/profile/');
    return toProfile(response.profile);
  }

  async update(patch: ProfilePatch): Promise<Profile> {
    const response = await this.http.request<{ profile: RawProfile }>('/api/v1/profile/', {
      method: 'PATCH',
      body: patch,
    });
    return toProfile(response.profile);
  }

  async uploadAvatar(file: Blob, fileName: string): Promise<Profile> {
    const tokens = await this.http.currentTokens();
    const body = multipartWithFile('avatar', file, fileName);
    const response = await fetch('/api/v1/profile/avatar/', {
      method: 'POST',
      credentials: 'include',
      headers: tokens?.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : {},
      body,
    });
    const parsed = (await response.json()) as { profile: RawProfile };
    return toProfile(parsed.profile);
  }
}

export class DevicesApi {
  constructor(private readonly http: LiniyaHttpClient) {}

  async list(): Promise<ManagedDevice[]> {
    const response = await this.http.request<{ devices: ManagedDevice[] }>('/api/v1/devices/');
    return response.devices;
  }

  /** Revoking another device needs the password again: it ends a live session. */
  async revoke(deviceId: Uuid, currentPassword: string): Promise<number> {
    const response = await this.http.request<{ revoked_sessions: number }>(
      `/api/v1/devices/${deviceId}/revoke/`,
      { method: 'POST', body: { current_password: currentPassword } },
    );
    return response.revoked_sessions;
  }

  async logoutOthers(currentPassword: string): Promise<number> {
    const response = await this.http.request<{ revoked_sessions: number }>(
      '/api/v1/devices/logout-others/',
      { method: 'POST', body: { current_password: currentPassword } },
    );
    return response.revoked_sessions;
  }
}

export class SearchApi {
  constructor(private readonly http: LiniyaHttpClient) {}

  async query(
    text: string,
    options: { types?: SearchEntityType[]; limit?: number; signal?: AbortSignal } = {},
  ): Promise<SearchResult[]> {
    const response = await this.http.request<{ query: string; results: SearchResult[] }>(
      '/api/v1/search/',
      {
        query: {
          q: text,
          types: (options.types ?? ['users', 'groups', 'channels']).join(','),
          limit: options.limit,
        },
        ...(options.signal ? { signal: options.signal } : {}),
      },
    );
    return response.results;
  }

  /**
   * Which address-book entries have an account, asked without naming them.
   *
   * Digests go up, not addresses: the people in someone's phone who do not use
   * «Линия» are never named to the server. See `emailDigest`.
   */
  async resolveContacts(
    emailHashes: string[],
    options: { signal?: AbortSignal } = {},
  ): Promise<ContactMatch[]> {
    if (emailHashes.length === 0) return [];
    const response = await this.http.request<{ matches: ContactMatch[] }>(
      '/api/v1/search/contacts/',
      {
        method: 'POST',
        body: { email_hashes: emailHashes },
        ...(options.signal ? { signal: options.signal } : {}),
      },
    );
    return response.matches;
  }
}

export class CallsApi {
  constructor(private readonly http: LiniyaHttpClient) {}

  /**
   * Fetch the ICE servers to use for the next call.
   *
   * Never cached across calls: relay credentials are time-limited, and the set
   * of servers is a deployment decision the client is not entitled to hardcode.
   */
  async iceConfiguration(): Promise<IceConfiguration> {
    return await this.http.request<IceConfiguration>('/api/v1/calls/ice-servers/');
  }
}

export class NotificationsApi {
  constructor(private readonly http: LiniyaHttpClient) {}

  async config(): Promise<NotificationConfig> {
    return await this.http.request<NotificationConfig>('/api/v1/notifications/config/');
  }

  async subscribe(subscription: unknown): Promise<{ subscription_id: Uuid }> {
    return await this.http.request<{ subscription_id: Uuid }>(
      '/api/v1/notifications/subscriptions/',
      { method: 'POST', body: subscription },
    );
  }

  async unsubscribe(subscriptionId: Uuid): Promise<void> {
    await this.http.request<void>(`/api/v1/notifications/subscriptions/${subscriptionId}/`, {
      method: 'DELETE',
    });
  }
}

export class SyncApi {
  constructor(private readonly http: LiniyaHttpClient) {}

  /**
   * Fetch durable events this device missed.
   *
   * The WebSocket says *something changed*; this says *what*. After a long
   * disconnect the socket cannot replay an unbounded backlog, so the client
   * walks the same per-device sequence over HTTP until it catches up.
   */
  async since(cursor: number, limit?: number): Promise<SyncPage> {
    return await this.http.request<SyncPage>('/api/v1/sync/', {
      query: { cursor, limit },
    });
  }
}

export class ConfigApi {
  constructor(private readonly http: LiniyaHttpClient) {}

  async get(): Promise<ClientConfig> {
    return await this.http.request<ClientConfig>('/api/v1/config/', { anonymous: true });
  }
}
